// src/components/Inputs/CheckboxInputs.jsx

'use client';

import { memo } from 'react';
import { FormControl, FormControlLabel, FormLabel, Checkbox } from '@mui/material';
import { capitalize } from '@/lib/functions.utils';
import { useFilters } from '@/contexts/Filters.context';
import Grid from '@mui/material/GridLegacy';


// Default rendering function FOR A STANDARD CHECKBOX ITEM (no Grid item wrapper here)
// The wrapper (Grid item) will be added by CheckboxInputs conditionally
const DefaultRenderItemContent = ({ value, displayLabel, isChecked, handleChange }) => (
  <FormControlLabel
    control={
      <Checkbox
        name={value}
        value={value}
        onChange={handleChange}
        checked={isChecked}
        color="primary"
      />
    }
    label={capitalize(displayLabel)}
    sx={{
      width: '100%', // Ensure label takes width for grid alignment
      '@media (pointer: fine)': {
        my: 0,
        '& .MuiCheckbox-root': {
          padding: '4px',
        },
      },
    }}
  />
);

// This is just a reference comparison for the default function
const DefaultRenderItem = DefaultRenderItemContent;

const CheckboxInputs = ({
  codeSetData,
  filterName,
  icon,
  legend,
  lang = 'en',
  renderItem = DefaultRenderItem, // Use the content renderer as default reference
  containerProps = {}, // Use this for styling the container (Grid or Div)
  itemProps = {},      // Pass down to each renderItem call
}) => {
  const { toggleInFilterSet, isInFilterSet, hasFilter } = useFilters();

  const handleChange = (event, value) => {
    toggleInFilterSet(filterName, value);
  };

  // Determine if we are using the default MUI Checkbox rendering
  const useDefaultRendering = renderItem === DefaultRenderItem;

  const checkboxItems = Object.entries(codeSetData).map(([key, data]) => {
    const value = data['en'];
    const displayLabel = data[lang];
    const isChecked = isInFilterSet(filterName, value);

    const renderProps = {
      key: key, // Use original key or value
      value: value,
      displayLabel: displayLabel,
      isChecked: isChecked,
      handleChange: (event) => handleChange(event, value),
      itemData: data,
      ...itemProps,
    };

    // Get the core item element from the render function
    const itemElement = renderItem(renderProps);

    // Conditionally wrap with Grid item ONLY if using default rendering
    if (useDefaultRendering) {
      // Default layout uses Grid item for xs=6 sm=6 layout
      return (
        <Grid item xs={6} sm={6} key={renderProps.key}>
          {itemElement}
        </Grid>
      );
    } else {
      // For custom renderers, return the element directly without Grid item wrapper
      // The custom renderer and container styling handle the layout
      return itemElement;
    }
  });

  const labelStyle = {
    color: hasFilter(filterName) ? '#1976D2' : '',
  };

  // Choose the container component based on rendering type
  const ContainerComponent = useDefaultRendering ? Grid : 'div';
  // Prepare container props. If it's Grid, add the 'container' prop.
  const finalContainerProps = useDefaultRendering
    ? { container: true, ...containerProps } // Add Grid container prop
    : { ...containerProps }; // Just use passed props for div

  return (
    <FormControl component="fieldset" sx={{ width: '100%' }}>
      <FormLabel component="legend" style={labelStyle}>
        {icon}
        {capitalize(legend)}
      </FormLabel>
      <ContainerComponent {...finalContainerProps}> {/* Dynamic Container */}
          {checkboxItems}
      </ContainerComponent>
    </FormControl>
  );
};

export default memo(CheckboxInputs);
